import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Users } from 'lucide-react';
import { TablePagination } from '@/components/TablePagination';
import { TableEmptyRow } from '@/components/TableEmptyRow';
import type { Member } from '@/types';
import type { TableColumn, ColumnSort, SortField } from './TableColumns';

interface MemberTableProps {
  members: Member[];
  columns: TableColumn[];
  sort: ColumnSort | null;
  onSort: (field: SortField) => void;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export function MemberTable({
  members,
  columns,
  sort,
  onSort,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: MemberTableProps) {
  const handleSort = (field: SortField) => {
    onSort(field);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-sm">
      <div className="min-h-0 flex-1 overflow-auto scrollbar-hide group-data-[table-locked=true]/page:overflow-y-hidden">
        <Table containerClassName="overflow-visible">
          <TableHeader className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/80">
            <TableRow className="border-gray-200 hover:bg-transparent">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={cn(column.className, 'h-11 px-4 text-left align-middle')}
                >
                  {column.renderHeader(sort, handleSort)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.length === 0 && (
              <TableEmptyRow
                columnCount={columns.length}
                icon={Users}
                title="No members found"
                hint="Add a new member to get started, or adjust your search."
              />
            )}
            {members.map((member) => (
              <TableRow
                key={member.id}
                className="border-gray-100 transition-colors hover:bg-gray-50/80"
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={cn(column.className, 'px-4 py-3 align-middle')}
                  >
                    {column.renderCell(member)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        itemLabel="members"
      />
    </div>
  );
}
